import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCalendarAlt, faBookOpen, faClock } from '@fortawesome/free-solid-svg-icons';
import { RRule } from 'rrule';
import '../../styles/group/CreateCalendarItemModal.css';
import CreateEventModal from './CreateEventModal';
import CreateClassModal from './CreateClassModal';
import calendarService from '../../services/calendarService';

// Helper function to format datetime for datetime-local input
// datetime-local inputs expect format: YYYY-MM-DDTHH:mm in LOCAL timezone
const formatDateTimeLocal = (dateString) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Helper function to parse RRULE string back to recurrence state
const parseRRuleToRecurrence = (rruleString) => {
  if (!rruleString) {
    return {
      frequency: 'none',
      interval: 1,
      weekdays: [],
      monthDay: 1,
      endType: 'never',
      endAfter: 10,
      endUntil: ''
    };
  }

  try {
    const rule = RRule.fromString(rruleString);
    const options = rule.origOptions;
    
    // Map RRule frequency to our format
    const freqMap = {
      [RRule.DAILY]: 'daily',
      [RRule.WEEKLY]: 'weekly',
      [RRule.MONTHLY]: 'monthly',
      [RRule.YEARLY]: 'yearly'
    };
    
    const recurrence = {
      frequency: freqMap[options.freq] || 'none',
      interval: options.interval || 1,
      weekdays: [],
      monthDay: 1,
      endType: 'never',
      endAfter: 10,
      endUntil: ''
    };
    
    // Parse weekdays for weekly recurrence
    if (options.byweekday && Array.isArray(options.byweekday)) {
      const dayMap = {
        0: 'MO', 1: 'TU', 2: 'WE', 3: 'TH', 4: 'FR', 5: 'SA', 6: 'SU'
      };
      recurrence.weekdays = options.byweekday.map(day => {
        const dayNum = typeof day === 'number' ? day : day.weekday;
        return dayMap[dayNum] || dayMap[(dayNum + 1) % 7];
      });
    }
    
    // Parse month day for monthly recurrence
    if (options.bymonthday && Array.isArray(options.bymonthday) && options.bymonthday.length > 0) {
      recurrence.monthDay = options.bymonthday[0];
    }
    
    // Parse end conditions
    if (options.count) {
      recurrence.endType = 'after';
      recurrence.endAfter = options.count;
    } else if (options.until) {
      recurrence.endType = 'until';
      const untilDate = new Date(options.until);
      recurrence.endUntil = untilDate.toISOString().split('T')[0];
    }
    
    return recurrence;
  } catch (error) {
    console.error('Error parsing RRULE:', error);
    return {
      frequency: 'none',
      interval: 1,
      weekdays: [],
      monthDay: 1,
      endType: 'never',
      endAfter: 10,
      endUntil: ''
    };
  }
};

const CreateCalendarItemModal = ({ 
  isOpen, 
  onClose, 
  groupId, 
  selectedSlot, 
  selectedEvent,
  onItemCreated,
  onItemUpdated,
  onItemDeleted,
  calendars = [],
  activeCalendar = null,
  initialStep = 'choose' // New prop to control initial step
}) => {
  const [step, setStep] = useState(initialStep);
  const [selectedCalendars, setSelectedCalendars] = useState(new Set(activeCalendar ? [activeCalendar] : []));
  const [isCalendarDropdownOpen, setIsCalendarDropdownOpen] = useState(false);
  const calendarDropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    all_day: false,
    location: '',
    color: '#3b82f6',
    reminder_enabled: false,
    reminder_minutes: 30,
    recurrence_rule: null
  });
  const [recurrence, setRecurrence] = useState({
    frequency: 'none', // 'none', 'daily', 'weekly', 'monthly', 'yearly', 'custom'
    interval: 1,
    weekdays: [], // For weekly: ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']
    monthDay: 1, // For monthly
    endType: 'never', // 'never', 'after', 'until'
    endAfter: 10, // Number of occurrences
    endUntil: '' // Date string
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Initialize selectedCalendars when modal opens or activeCalendar changes
  useEffect(() => {
    if (isOpen && activeCalendar) {
      setSelectedCalendars(new Set([activeCalendar]));
    }
    // Set initial step when modal opens
    if (isOpen) {
      setStep(initialStep);
    }
    // Reset step when modal closes
    if (!isOpen) {
      setStep('choose');
    }
  }, [isOpen, activeCalendar, initialStep]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarDropdownRef.current && !calendarDropdownRef.current.contains(event.target)) {
        setIsCalendarDropdownOpen(false);
      }
    };

    if (isCalendarDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarDropdownOpen]);

  // Initialize form data when slot or event is selected
  useEffect(() => {
    console.log('📝 CreateCalendarItemModal - useEffect triggered:', {
      hasSelectedSlot: !!selectedSlot,
      hasSelectedEvent: !!selectedEvent,
      selectedEventType: selectedEvent?.resource?.type,
      selectedEventData: selectedEvent?.resource?.data
    });
    
    if (selectedSlot && !selectedEvent) {
      // Creating new item from selected slot - go directly to 'other' step
      const startTime = new Date(selectedSlot.start);
      // Set end time to 1 hour after start time for better UX
      const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // Add 1 hour
      
      setFormData(prev => ({
        ...prev,
        start_time: formatDateTimeLocal(startTime),
        end_time: formatDateTimeLocal(endTime)
      }));
      setStep('other'); // Skip the 'choose' step
    } else if (selectedEvent && selectedEvent.resource?.type === 'other') {
      const item = selectedEvent.resource.data;
      console.log('📝 Editing calendar item:', {
        id: item.id,
        title: item.title,
        start_time: item.start_time,
        end_time: item.end_time,
        recurrence_rule: item.recurrence_rule
      });
      
      setFormData({
        title: item.title || '',
        description: item.description || '',
        start_time: formatDateTimeLocal(item.start_time),
        end_time: formatDateTimeLocal(item.end_time),
        all_day: item.all_day || false,
        location: item.location || '',
        color: item.color || '#3b82f6',
        reminder_enabled: item.reminder_enabled || false,
        reminder_minutes: item.reminder_minutes || 30
      });
      
      // Parse and set recurrence data
      const parsedRecurrence = parseRRuleToRecurrence(item.recurrence_rule);
      console.log('📝 Parsed recurrence:', parsedRecurrence);
      setRecurrence(parsedRecurrence);
      
      // If editing an existing item with calendar associations, set those
      if (item.calendarIds && Array.isArray(item.calendarIds) && item.calendarIds.length > 0) {
        setSelectedCalendars(new Set(item.calendarIds));
      }
      setStep('other'); // Go directly to edit form
    }
  }, [selectedSlot, selectedEvent]);

  // Update the required weekday when start_time changes during weekly recurrence
  useEffect(() => {
    if (recurrence.frequency === 'weekly' && formData.start_time) {
      const startDate = new Date(formData.start_time);
      const dayOfWeek = startDate.getDay();
      const dayMap = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
      const requiredDay = dayMap[dayOfWeek];
      
      // If the required day isn't in the weekdays array, add it
      if (!recurrence.weekdays.includes(requiredDay)) {
        setRecurrence(prev => {
          // Keep all other days, add the new required day
          const otherDays = prev.weekdays.filter(day => day !== requiredDay);
          return {
            ...prev,
            weekdays: [requiredDay, ...otherDays]
          };
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.start_time]); // Only trigger when start_time changes

  const handleChooseType = (type) => {
    setStep(type);
  };

  const handleCalendarToggle = (calendarId) => {
    const newSelected = new Set(selectedCalendars);
    if (newSelected.has(calendarId)) {
      newSelected.delete(calendarId);
    } else {
      newSelected.add(calendarId);
    }
    setSelectedCalendars(newSelected);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // If start_time is being changed, automatically update end_time to 1 hour later
    if (name === 'start_time' && value) {
      const startDate = new Date(value);
      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Add 1 hour
      
      setFormData(prev => ({
        ...prev,
        start_time: value,
        end_time: formatDateTimeLocal(endDate)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleRecurrenceChange = (field, value) => {
    // If switching to weekly frequency, auto-select the weekday of the start date
    if (field === 'frequency' && value === 'weekly' && formData.start_time) {
      const startDate = new Date(formData.start_time);
      const dayOfWeek = startDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      
      // Map day number to our weekday format
      const dayMap = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
      const weekday = dayMap[dayOfWeek];
      
      setRecurrence(prev => ({
        ...prev,
        frequency: value,
        weekdays: [weekday] // Auto-select the start date's weekday
      }));
    } else if (field === 'frequency' && value === 'monthly' && formData.start_time) {
      // Auto-select the day of the month from start date
      const startDate = new Date(formData.start_time);
      const dayOfMonth = startDate.getDate();
      
      setRecurrence(prev => ({
        ...prev,
        frequency: value,
        monthDay: dayOfMonth // Auto-select the start date's day of month
      }));
    } else {
      setRecurrence(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const generateRRule = () => {
    if (recurrence.frequency === 'none') return null;

    const options = {
      freq: RRule[recurrence.frequency.toUpperCase()],
      interval: recurrence.interval,
      dtstart: new Date(formData.start_time)
    };

    // Add weekdays for weekly recurrence
    if (recurrence.frequency === 'weekly' && recurrence.weekdays.length > 0) {
      options.byweekday = recurrence.weekdays.map(day => RRule[day]);
    }

    // Add month day for monthly recurrence
    if (recurrence.frequency === 'monthly' && recurrence.monthDay) {
      options.bymonthday = recurrence.monthDay;
    }

    // Add end conditions
    if (recurrence.endType === 'after') {
      options.count = recurrence.endAfter;
    } else if (recurrence.endType === 'until') {
      options.until = new Date(recurrence.endUntil);
    }

    const rule = new RRule(options);
    
    // Return the full string including DTSTART for proper parsing
    // Format: DTSTART:20251021T120000Z\nFREQ=WEEKLY;BYDAY=TU
    return rule.toString();
  };

  const handleSubmitOther = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate calendar selection
    if (selectedCalendars.size === 0) {
      setError('Please select at least one calendar');
      setLoading(false);
      return;
    }

    try {
      const rruleString = generateRRule();
      
      const itemData = {
        ...formData,
        group_id: groupId,
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString(),
        recurrence_rule: rruleString,
        calendarIds: Array.from(selectedCalendars)
      };

      if (selectedEvent && selectedEvent.resource?.type === 'other') {
        // Update existing item
        await calendarService.updateCalendarItem(
          selectedEvent.resource.data.id,
          itemData
        );
        console.log('✅ Calendar item updated successfully');
        // Call the update callback
        if (onItemUpdated) {
          onItemUpdated();
        }
      } else {
        // Create new item
        await calendarService.createCalendarItem(itemData);
        console.log('✅ Calendar item created successfully');
        // Call the create callback
        if (onItemCreated) {
          onItemCreated();
        }
      }
    } catch (err) {
      console.error('Error saving calendar item:', err);
      setError(err.message || 'Failed to save calendar item');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOther = async () => {
    if (!selectedEvent || selectedEvent.resource?.type !== 'other') return;
    
    const itemId = selectedEvent.resource.data.id;
    console.log('🗑️ Attempting to delete calendar item:', itemId);
    console.log('🗑️ Group ID:', groupId);
    
    if (!itemId) {
      console.error('❌ No item ID found in selectedEvent:', selectedEvent);
      setError('Cannot delete: Item ID not found');
      return;
    }

    setLoading(true);
    try {
      await calendarService.deleteCalendarItem(groupId, itemId);
      console.log('✅ Calendar item deleted successfully');
      // Close delete confirmation
      setShowDeleteConfirmation(false);
      // Call the delete callback
      if (onItemDeleted) {
        onItemDeleted();
      }
    } catch (err) {
      console.error('Error deleting calendar item:', err);
      setError(err.message || 'Failed to delete calendar item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // If editing an event or class, show their respective modals
  if (selectedEvent && selectedEvent.resource?.type === 'event') {
    return (
      <CreateEventModal
        isOpen={isOpen}
        onClose={onClose}
        groupId={groupId}
        event={selectedEvent.resource.data}
        onEventCreated={onItemCreated}
      />
    );
  }

  if (selectedEvent && selectedEvent.resource?.type === 'class') {
    return (
      <CreateClassModal
        isOpen={isOpen}
        onClose={onClose}
        groupId={groupId}
        classData={selectedEvent.resource.data}
        onClassCreated={onItemCreated}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header - Fixed */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-900">
              {step === 'choose' && 'What would you like to add?'}
              {step === 'other' && (selectedEvent ? 'Edit Calendar Item' : 'Add to Calendar')}
            </h3>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
            >
              <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Step 1: Choose type */}
          {step === 'choose' && (
            <div className="grid grid-cols-1 gap-4">
              <button 
                type="button"
                onClick={() => handleChooseType('event')}
                className="group flex items-start p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 text-left"
              >
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors mr-4 flex-shrink-0">
                  <FontAwesomeIcon icon={faCalendarAlt} className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Event</h4>
                  <p className="text-sm text-gray-600">Community gatherings, special occasions, and social events</p>
                </div>
              </button>

              <button 
                type="button"
                onClick={() => handleChooseType('class')}
                className="group flex items-start p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all duration-200 text-left"
              >
                <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors mr-4 flex-shrink-0">
                  <FontAwesomeIcon icon={faBookOpen} className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Class</h4>
                  <p className="text-sm text-gray-600">Educational sessions, lessons, and learning activities</p>
                </div>
              </button>

              <button 
                type="button"
                onClick={() => handleChooseType('other')}
                className="group flex items-start p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all duration-200 text-left"
              >
                <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors mr-4 flex-shrink-0">
                  <FontAwesomeIcon icon={faClock} className="w-6 h-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Other</h4>
                  <p className="text-sm text-gray-600">Reminders, tasks, appointments, and miscellaneous items</p>
                </div>
              </button>
            </div>
          )}

          {/* Step 2a: Create Event */}
          {step === 'event' && (
            <CreateEventModal
              isOpen={true}
              onClose={onClose}
              groupId={groupId}
              initialData={selectedSlot ? {
                date_time: selectedSlot.start,
                duration: Math.round((selectedSlot.end - selectedSlot.start) / 60000)
              } : null}
              onEventCreated={onItemCreated}
            />
          )}

          {/* Step 2b: Create Class */}
          {step === 'class' && (
            <CreateClassModal
              isOpen={true}
              onClose={onClose}
              groupId={groupId}
              initialData={selectedSlot ? {
                date_time: selectedSlot.start,
                duration: Math.round((selectedSlot.end - selectedSlot.start) / 60000)
              } : null}
              onClassCreated={onItemCreated}
            />
          )}

          {/* Step 2c: Create/Edit Other */}
          {step === 'other' && (
            <form onSubmit={handleSubmitOther} className="space-y-5 p-1">
              {/* Calendar Selection - Moved to top */}
              {calendars.length > 0 && (
                <div className="space-y-2" ref={calendarDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700">
                    Add to Calendar(s) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    {/* Dropdown Button */}
                    <button
                      type="button"
                      onClick={() => setIsCalendarDropdownOpen(!isCalendarDropdownOpen)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-left focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {selectedCalendars.size === 0 ? (
                          <span className="text-sm text-gray-400">Select calendar(s)</span>
                        ) : selectedCalendars.size === 1 ? (
                          <div className="flex items-center gap-2 min-w-0">
                            <div 
                              className="w-3 h-3 rounded-full flex-shrink-0" 
                              style={{ backgroundColor: calendars.find(c => c.id === Array.from(selectedCalendars)[0])?.color || '#3b82f6' }}
                            />
                            <span className="text-sm text-gray-900 truncate">
                              {calendars.find(c => c.id === Array.from(selectedCalendars)[0])?.name}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-900">
                            {selectedCalendars.size} calendars selected
                          </span>
                        )}
                      </div>
                      <svg 
                        className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isCalendarDropdownOpen ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {isCalendarDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {calendars.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500">
                            No calendars available
                          </div>
                        ) : (
                          <div className="py-1">
                            {calendars.map(calendar => (
                              <label 
                                key={calendar.id} 
                                className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedCalendars.has(calendar.id)}
                                  onChange={() => handleCalendarToggle(calendar.id)}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                                />
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <div 
                                    className="w-3 h-3 rounded-full flex-shrink-0" 
                                    style={{ backgroundColor: calendar.color || '#3b82f6' }}
                                  />
                                  <span className="text-sm text-gray-900 truncate">{calendar.name}</span>
                                  {calendar.is_default && (
                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded flex-shrink-0">
                                      Default
                                    </span>
                                  )}
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedCalendars.size === 0 && (
                    <p className="text-xs text-red-600 mt-1">Please select at least one calendar</p>
                  )}
                </div>
              )}

              {/* Title Field */}
              <div className="space-y-2">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter event title"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400"
                />
              </div>

              {/* Description Field */}
              <div className="space-y-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Add a description (optional)"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400 resize-none"
                />
              </div>

              {/* Date and Time Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="start_time" className="block text-sm font-medium text-gray-700">
                    Start Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    id="start_time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="end_time" className="block text-sm font-medium text-gray-700">
                    End Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    id="end_time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900"
                  />
                </div>
              </div>

              {/* All Day Checkbox */}
              <div className="flex items-center space-x-3 p-3.5 bg-gray-50 rounded-lg border border-gray-200">
                <input
                  type="checkbox"
                  id="all_day"
                  name="all_day"
                  checked={formData.all_day}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                />
                <label htmlFor="all_day" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                  All day event
                </label>
              </div>

              {/* Location Field */}
              <div className="space-y-2">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                  Location
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Add a location (optional)"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400"
                />
              </div>

              {/* Recurrence Section */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Repeat
                </label>
                <select
                  value={recurrence.frequency}
                  onChange={(e) => handleRecurrenceChange('frequency', e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 bg-white cursor-pointer"
                >
                  <option value="none">Does not repeat</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>

                {/* Weekly Options */}
                {recurrence.frequency === 'weekly' && (
                  <div className="space-y-2 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-fadeIn">
                    <label className="block text-sm font-medium text-gray-700">
                      Repeat on
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { label: 'S', value: 'SU' },
                        { label: 'M', value: 'MO' },
                        { label: 'T', value: 'TU' },
                        { label: 'W', value: 'WE' },
                        { label: 'T', value: 'TH' },
                        { label: 'F', value: 'FR' },
                        { label: 'S', value: 'SA' }
                      ].map((day) => {
                        // Get the start date's weekday to make it required
                        const startDate = formData.start_time ? new Date(formData.start_time) : null;
                        const startDayOfWeek = startDate ? startDate.getDay() : -1;
                        const dayMap = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
                        const startDayValue = startDayOfWeek >= 0 ? dayMap[startDayOfWeek] : null;
                        const isStartDay = day.value === startDayValue;
                        const isSelected = recurrence.weekdays.includes(day.value);
                        
                        return (
                          <button
                            key={day.value}
                            type="button"
                            onClick={() => {
                              // Don't allow unchecking the start day
                              if (isStartDay) return;
                              
                              const newWeekdays = isSelected
                                ? recurrence.weekdays.filter(d => d !== day.value)
                                : [...recurrence.weekdays, day.value];
                              handleRecurrenceChange('weekdays', newWeekdays);
                            }}
                            disabled={isStartDay}
                            className={`w-10 h-10 rounded-full font-medium text-sm transition-all ${
                              isSelected
                                ? isStartDay 
                                  ? 'bg-blue-600 text-white cursor-not-allowed opacity-90 ring-2 ring-blue-400'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                            }`}
                            title={isStartDay ? 'Event starts on this day (required)' : ''}
                          >
                            {day.label}
                          </button>
                        );
                      })}
                    </div>
                    {formData.start_time && (
                      <p className="text-xs text-gray-500 mt-2">
                        The selected start day is required and cannot be removed
                      </p>
                    )}
                  </div>
                )}

                {/* Monthly Options */}
                {recurrence.frequency === 'monthly' && (
                  <div className="space-y-2 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-fadeIn">
                    <label htmlFor="monthDay" className="block text-sm font-medium text-gray-700">
                      Day of month
                    </label>
                    <input
                      type="number"
                      id="monthDay"
                      min="1"
                      max="31"
                      value={recurrence.monthDay}
                      onChange={(e) => handleRecurrenceChange('monthDay', parseInt(e.target.value))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900"
                    />
                  </div>
                )}

                {/* End Conditions */}
                {recurrence.frequency !== 'none' && (
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-fadeIn">
                    <label className="block text-sm font-medium text-gray-700">
                      Ends
                    </label>
                    
                    <div className="space-y-3">
                      {/* Never */}
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="radio"
                          name="endType"
                          value="never"
                          checked={recurrence.endType === 'never'}
                          onChange={(e) => handleRecurrenceChange('endType', e.target.value)}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">Never</span>
                      </label>

                      {/* After X occurrences */}
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="endType"
                          value="after"
                          checked={recurrence.endType === 'after'}
                          onChange={(e) => handleRecurrenceChange('endType', e.target.value)}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">After</span>
                        <input
                          type="number"
                          min="1"
                          value={recurrence.endAfter}
                          onChange={(e) => {
                            handleRecurrenceChange('endAfter', parseInt(e.target.value));
                            handleRecurrenceChange('endType', 'after');
                          }}
                          className="w-20 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 text-sm"
                        />
                        <span className="text-sm text-gray-700">occurrences</span>
                      </div>

                      {/* Until date */}
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="endType"
                          value="until"
                          checked={recurrence.endType === 'until'}
                          onChange={(e) => handleRecurrenceChange('endType', e.target.value)}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">On</span>
                        <input
                          type="date"
                          value={recurrence.endUntil}
                          onChange={(e) => {
                            handleRecurrenceChange('endUntil', e.target.value);
                            handleRecurrenceChange('endType', 'until');
                          }}
                          className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div>
                  {selectedEvent && (
                    <>
                      {console.log('🗑️ Rendering delete button for event:', selectedEvent?.resource?.data?.id)}
                      <button
                        type="button"
                        onClick={() => {
                          console.log('🗑️ Delete button clicked!');
                          setShowDeleteConfirmation(true);
                        }}
                        disabled={loading}
                        className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <button 
                    type="button" 
                    onClick={onClose}
                    disabled={loading}
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    disabled={loading || selectedCalendars.size === 0}
                    title={selectedCalendars.size === 0 ? 'Please select at least one calendar' : ''}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <span>{selectedEvent ? 'Update Event' : 'Create Event'}</span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 animate-fadeIn">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Delete Calendar Item
            </h3>
            
            <p className="text-sm text-gray-600 text-center mb-6">
              Are you sure you want to delete "{formData.title || 'this item'}"? This action cannot be undone.
            </p>
            
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirmation(false)}
                disabled={loading}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteOther}
                disabled={loading}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Delete</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateCalendarItemModal;
